import { z } from "zod";
import { entityIdSchema, INT32_MAX } from "./common";
import { ProjectExpectedValuesSchema } from "./entities/project";

/** Exact project snapshot stamp shown to the user before native CPP/RAMS generation. */
export const GenerationProjectInputReviewSchema = z.object({
  documentInputRevision: z.number().int().min(0).max(INT32_MAX),
  projectUpdatedAt: z.string().datetime(),
  hazardCatalogRevision: z.number().int().min(0).max(INT32_MAX),
  accountCustomHazardRevision: z.number().int().min(0).max(INT32_MAX),
});
/** Frozen request stamp stays strict even though read responses are additive-tolerant. */
export const GenerationProjectInputReviewStrictSchema = GenerationProjectInputReviewSchema.strict();
export type GenerationProjectInputReview = z.infer<typeof GenerationProjectInputReviewSchema>;

export const GenerationProjectReviewDetailSchema = z.object({
  label: z.string().min(1),
  value: z.string(),
});
export const GenerationProjectReviewDetailStrictSchema = GenerationProjectReviewDetailSchema.strict();

export const GenerationProjectReviewItemSchema = z.object({
  key: z.string().min(1),
  title: z.string().min(1),
  details: z.array(GenerationProjectReviewDetailSchema),
});
export const GenerationProjectReviewItemStrictSchema = z.object({
  key: z.string().min(1),
  title: z.string().min(1),
  details: z.array(GenerationProjectReviewDetailStrictSchema),
}).strict();

export const GenerationProjectReviewSectionSchema = z.object({
  key: z.string().min(1),
  title: z.string().min(1),
  items: z.array(GenerationProjectReviewItemSchema),
});
export const GenerationProjectReviewSectionStrictSchema = z.object({
  key: z.string().min(1),
  title: z.string().min(1),
  items: z.array(GenerationProjectReviewItemStrictSchema),
}).strict();

export const GenerationProjectReviewImageSchema = z.object({
  id: entityIdSchema,
  title: z.string(),
  url: z.string(),
  includeInCpp: z.boolean(),
  extractedFromPdf: z.boolean(),
});
export const GenerationProjectReviewImageStrictSchema = GenerationProjectReviewImageSchema.strict();

/** Full, account-authorized relational input review for CPP or RAMS only. */
export const GenerationProjectReviewResponseSchema = z.object({
  projectId: entityIdSchema,
  documentType: z.enum(["CPP", "RAMS"]),
  review: GenerationProjectInputReviewSchema,
  projectExpectedValues: ProjectExpectedValuesSchema.strip(),
  sections: z.array(GenerationProjectReviewSectionSchema),
  images: z.array(GenerationProjectReviewImageSchema),
});
export type GenerationProjectReviewResponse = z.infer<typeof GenerationProjectReviewResponseSchema>;

/** Deep-strict schema for server egress; keep it paired with the tolerant mobile read schema. */
export const GenerationProjectReviewResponseStrict = z.object({
  projectId: entityIdSchema,
  documentType: z.enum(["CPP", "RAMS"]),
  review: GenerationProjectInputReviewStrictSchema,
  projectExpectedValues: ProjectExpectedValuesSchema,
  sections: z.array(GenerationProjectReviewSectionStrictSchema),
  images: z.array(GenerationProjectReviewImageStrictSchema),
}).strict();
